// ---- sFlow network-traffic analysis (BETA) -------------------------------

import { useQuery } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';

export interface SFlowSummary {
  total_bandwidth_mbps?: number;
  total_bps?: number;
  total_bytes?: number;
  total_packets?: number;
  total_pps?: number;
  unique_destinations?: number;
  unique_sources?: number;
}

export interface SFlowLog {
  timestamp?: string;
  src_ip?: string;
  src_port?: number;
  dst_ip?: string;
  dst_port?: number;
  protocol?: number;
  bytes?: number;
  packets?: number;
  bps?: number;
  pps?: number;
  severity?: string;
  attack_type?: string;
}

export interface SFlowSource {
  src_ip?: string;
  connection_count?: number;
  total_bytes?: number;
  total_packets?: number;
  protocols?: number[];
  ports?: number[];
  attack_types?: string[];
  latest_timestamp?: string;
}

/** Aggregated top source IPs talking to the instance (BETA; 404 when sFlow off). */
export function useInstanceSflowSources(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: { limit?: number },
) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'instances', instanceId ?? '', 'sflow-sources', JSON.stringify(query ?? {})],
    enabled: !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ sources?: SFlowSource[]; summary?: SFlowSummary; total_unique_sources?: number }>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/sflow_sources`), { params: query }),
      );
      return { sources: body.sources ?? [], summary: body.summary ?? {}, total: body.total_unique_sources ?? 0 };
    },
  });
}

/** Recent flow records for the instance (BETA; 404 when sFlow off). */
export function useInstanceSflow(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: { limit?: number; direction?: 'incoming' | 'outgoing' },
) {
  return useQuery({
    queryKey: onKeys.instanceSflow(serviceId ?? '', instanceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ data?: SFlowLog[]; summary?: SFlowSummary }>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/sflow`), { params: query }),
      );
      return { logs: body.data ?? [], summary: body.summary ?? {} };
    },
  });
}
